"use client";

import React from "react";
import Link from "next/link";
import { GiCheckMark } from "react-icons/gi";
import { FiRefreshCw } from "react-icons/fi";
import SnappedWidth from "~/components/ui/snapped-width";
import RowPoints from "~/components/detailed-stats/row-points";

import type { DetailedStatisticsCategory } from "~/lib/detailedStats";
import { StatsCategoryType, formatPercentage } from "~/lib/detailedStats";

type Props = {
  category: DetailedStatisticsCategory;
  denominator: number;
  isFullMode: boolean;
  problemSetId: number | string;
  onClose: () => void;
};

export default function CategoryItem({
  category: cat,
  denominator,
  isFullMode,
  problemSetId,
  onClose,
}: Props) {
  const earnedPoints = cat.earnedPoints ?? 0;
  const maxPoints = cat.maxPoints ?? 0;

  return (
    <div className="mb-3">
      <div className="grid grid-cols-[auto_1fr] items-start gap-4">
        <SnappedWidth className="flex items-center gap-1 whitespace-nowrap">
          {cat.type === StatsCategoryType.WIN && (
            <GiCheckMark key="icon" className="text-green-500" />
          )}
          {cat.type === StatsCategoryType.DRAW && (
            <span key="icon" className="text-yellow-300">
              ½
            </span>
          )}
          {cat.type === StatsCategoryType.EXTERNAL_HINT && (
            <span key="icon" className="text-blue-500">
              ½
            </span>
          )}

          {(cat.retryCount ?? 0) > 0 && (
            <div className="flex items-center text-pink-400">
              <FiRefreshCw />
              <span className="ml-1">{String(cat.retryCount)}</span>
            </div>
          )}

          <span>:</span>
        </SnappedWidth>

        <div className="flex items-start gap-2">
          <span>{cat.count}</span>
          <span>({formatPercentage(cat.count, denominator)}%)</span>
          <RowPoints earnedPoints={earnedPoints} maxPoints={maxPoints} />

          {isFullMode &&
            (cat.type !== StatsCategoryType.WIN || (cat.retryCount ?? 0) > 0) &&
            (cat.tasks ?? []).length > 0 && (
              <span className="ml-2 inline-block text-sm text-muted-foreground">
                (
                {cat.tasks.map(
                  (t: { problemId: number; name: string }, i: number) => (
                    <React.Fragment key={`task-${t.problemId}`}>
                      <Link
                        href={`/problem-sets/${problemSetId}/problems/${t.problemId}`}
                        onClick={() => onClose()}
                        className="text-xs text-white underline"
                      >
                        {t.name}
                      </Link>
                      {i < (cat.tasks ?? []).length - 1 ? ", " : null}
                    </React.Fragment>
                  ),
                )}
                )
              </span>
            )}
        </div>
      </div>
    </div>
  );
}
